import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import BarChartIcon from '@mui/icons-material/BarChart';

// Configuration automatique de l'API
// Utilise HTTPS en production pour éviter les problèmes de Mixed Content
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.hostname}/api`;

export default function MatchDetail() {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const [loading, setLoading] = useState(true);
  const [matchData, setMatchData] = useState(null);
  const [stats, setStats] = useState([]);
  const [playerComposition, setPlayerComposition] = useState({});
  const [substitutes, setSubstitutes] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [compositionData, setCompositionData] = useState([]);
  const [substitutions, setSubstitutions] = useState([]);


  // Charger les données initiales
  useEffect(() => {
    // Changer le titre de la page
    document.title = 'ESMANAGER - DÉTAIL MATCH';
    
    loadMatchData();
  }, [matchId]);

  // Rafraîchissement automatique des données si le match est en cours
  useEffect(() => {
    if (!matchData || matchData.status !== 'en_cours') {
      return;
    }

    // Rafraîchir les données toutes les 3 secondes (sans afficher le loading)
    const interval = setInterval(() => {
      loadMatchData(false);
    }, 3000);

    // Rafraîchir quand l'utilisateur revient sur l'onglet
    const handleFocus = () => {
      if (matchData?.status === 'en_cours') {
        loadMatchData(false);
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [matchData?.status]);


  const loadMatchData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      // Charger les infos du match
      const matchResponse = await fetch(`${API_URL}/matches/${matchId}`);
      const match = await matchResponse.json();
      setMatchData(match);

      // Charger les statistiques
      const statsResponse = await fetch(`${API_URL}/match-stats-simple/${matchId}`);
      const statsData = await statsResponse.json();
      setStats(statsData);

      // Charger la composition du match
      const compoResponse = await fetch(`${API_URL}/composition/${match.id_composition}`);
      const compoData = await compoResponse.json();
      setCompositionData(compoData);

      // Charger les joueurs avec leurs positions exactes
      const playersResponse = await fetch(`${API_URL}/match-players/${matchId}`);
      const playersData = await playersResponse.json();
      setPlayerComposition(playersData.composition || {});
      setSubstitutes(playersData.substitutes || []);
      setAllPlayers(playersData.allPlayers || []);

      // Charger les changements
      const subsResponse = await fetch(`${API_URL}/match-subs/${matchId}`);
      const subsData = await subsResponse.json();
      setSubstitutions(subsData);


    } catch (error) {
      console.error('Erreur chargement données match:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };



  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleShare = () => {
    const liveUrl = `${window.location.origin}/live`;
    navigator.clipboard.writeText(liveUrl).then(() => {
      alert(`Lien copié !\n\n${liveUrl}\n\nPartagez ce lien pour que les spectateurs suivent le match en direct.`);
    }).catch((err) => {
      console.error('Erreur copie lien:', err);
      alert(`Lien live :\n${liveUrl}`);
    });
  };

  const getStatsSummary = () => {
    const summary = {};
    stats.forEach(stat => {
      if (!summary[stat.action]) {
        summary[stat.action] = { home: 0, away: 0 };
      }
      if (stat.team === 'home') {
        summary[stat.action].home++;
      } else {
        summary[stat.action].away++;
      }
    });
    
    // Gérer les fautes : comparer "Faut subit" (home) vs "Faut commise" (away)
    if (summary['Faut subit'] || summary['Faut commise']) {
      summary['Fautes'] = {
        home: summary['Faut subit']?.home || 0,
        away: summary['Faut commise']?.home || 0
      };
      // Supprimer les anciennes entrées
      delete summary['Faut subit'];
      delete summary['Faut commise'];
    }
    
    return summary;
  };

  const getGoalsWithDetails = () => {
    const goals = [];
    const buts = stats.filter(stat => stat.action === 'BUT');
    
    buts.forEach(but => {
      const minuteOnly = but.minute.split(':')[0];
      
      if (but.team === 'home') {
        // Pour notre équipe : grouper par joueur
        const scorer = but.id_player ? `${but.firstname} ${but.lastname}` : 'Joueur inconnu';
        const existingGoal = goals.find(g => g.team === 'home' && g.scorer === scorer);
        
        if (existingGoal) {
          existingGoal.minutes.push(minuteOnly);
        } else {
          // Chercher la passe décisive correspondante
          const assist = stats.find(stat => 
            stat.action === 'PASSE DECISIVE' && 
            stat.minute === but.minute && 
            stat.team === but.team
          );
          
          goals.push({
            team: but.team,
            scorer: scorer,
            minutes: [minuteOnly],
            assists: assist && assist.id_player ? [`${assist.firstname} ${assist.lastname}`] : []
          });
        }
      } else {
        // Pour l'équipe adverse : chaque but individuellement
        // Chercher la passe décisive correspondante
        const assist = stats.find(stat => 
          stat.action === 'PASSE DECISIVE' && 
          stat.minute === but.minute && 
          stat.team === but.team
        );
        
        goals.push({
          team: but.team,
          scorer: '', // Pas de nom pour l'équipe adverse
          minutes: [minuteOnly],
          assists: assist && assist.id_player ? [`${assist.firstname} ${assist.lastname}`] : []
        });
      }
    });
    
    // Trier par minute
    return goals.sort((a, b) => {
      const minA = parseInt(a.minutes[0]);
      const minB = parseInt(b.minutes[0]);
      return minA - minB;
    });
  };

  const getChronologicalEvents = () => {
    const events = [];
    
    // Ajouter les buts
    stats.filter(stat => stat.action === 'BUT').forEach(but => {
      const minuteOnly = but.minute.split(':')[0];
      const scorer = but.id_player ? `${but.firstname} ${but.lastname}` : (but.team === 'home' ? 'Joueur inconnu' : '');
      
      // Chercher la passe décisive correspondante
      const assist = stats.find(stat => 
        stat.action === 'PASSE DECISIVE' && 
        stat.minute === but.minute && 
        stat.team === but.team
      );
      
      events.push({
        type: 'goal',
        minute: minuteOnly,
        team: but.team,
        player: scorer,
        assist: assist && assist.id_player ? `${assist.firstname} ${assist.lastname}` : null
      });
    });
    
    // Ajouter les changements
    substitutions.forEach(sub => {
      const minuteOnly = sub.minute.split(':')[0];
      const playerInName = sub.player_in_name || 'Joueur inconnu';
      const playerOutName = sub.player_out_name || 'Joueur inconnu';
      
      events.push({
        type: 'substitution',
        minute: minuteOnly,
        team: 'home', // Les changements sont toujours pour notre équipe
        playerIn: playerInName,
        playerOut: playerOutName
      });
    });
    
    // Ajouter les cartons
    stats.filter(stat => stat.action === 'Carton Jaune' || stat.action === 'Carton Rouge').forEach(card => {
      const minuteOnly = card.minute.split(':')[0];
      const player = card.id_player ? `${card.firstname} ${card.lastname}` : (card.team === 'home' ? 'Joueur inconnu' : '');
      
      events.push({
        type: 'card',
        minute: minuteOnly,
        team: card.team,
        player: player,
        cardType: card.action === 'Carton Jaune' ? 'yellow' : 'red'
      });
    });
    
    // Trier par minute (ordre décroissant - plus récent en haut)
    return events.sort((a, b) => parseInt(b.minute) - parseInt(a.minute));
  };

  // Générer les positions depuis la composition (comme dans Composition.js)
  const generatePositions = () => {
    if (!compositionData || compositionData.length === 0) return [];
    
    const positions = [];
    let positionIndex = 0;
    
    compositionData.forEach((poste) => {
      const { nbPlayer_poste, poste_label } = poste;
      
      for (let i = 0; i < nbPlayer_poste; i++) {
        let x = 50;
        let y = 50;
        
        if (poste_label === 'Attaquant') {
          if (nbPlayer_poste === 1) {
            x = 50;
            y = 10;
          } else if (nbPlayer_poste === 2) {
            x = i === 0 ? 30 : 70;
            y = 10;
          } else if (nbPlayer_poste === 3) {
            if (i === 1) {
              x = 50;
              y = 5;
            } else {
              x = i === 0 ? 20 : 80;
              y = 15;
            }
          } else if (nbPlayer_poste === 4) {
            if (i < 2) {
              x = i === 0 ? 15 : 85;
              y = 5;
            } else {
              x = i === 2 ? 30 : 70;
              y = 15;
            }
          }
        } else if (poste_label === 'Milieu') {
          if (nbPlayer_poste === 1) {
            x = 50;
            y = 35;
          } else if (nbPlayer_poste === 2) {
            x = i === 0 ? 30 : 70;
            y = 35;
          } else if (nbPlayer_poste === 3) {
            if (i === 1) {
              x = 50;
              y = 40;
            } else {
              x = i === 0 ? 20 : 80;
              y = 30;
            }
          } else if (nbPlayer_poste === 4) {
            if (i < 2) {
              x = i === 0 ? 15 : 85;
              y = 30;
            } else {
              x = i === 2 ? 30 : 70;
              y = 40;
            }
          } else if (nbPlayer_poste === 5) {
            if (i < 3) {
              x = 15 + (i * 35);
              y = 30;
            } else {
              x = i === 3 ? 30 : 70;
              y = 45;
            }
          }
        } else if (poste_label === 'Défenseur') {
          if (nbPlayer_poste === 1) {
            x = 50;
            y = 65;
          } else if (nbPlayer_poste === 2) {
            x = i === 0 ? 30 : 70;
            y = 65;
          } else if (nbPlayer_poste === 3) {
            if (i === 1) {
              x = 50;
              y = 70;
            } else {
              x = i === 0 ? 20 : 80;
              y = 60;
            }
          } else if (nbPlayer_poste === 4) {
            if (i < 2) {
              x = i === 0 ? 15 : 85;
              y = 60;
            } else {
              x = i === 2 ? 30 : 70;
              y = 70;
            }
          } else if (nbPlayer_poste === 5) {
            if (i < 2) {
              x = i === 0 ? 15 : 85;
              y = 60;
            } else {
              x = 20 + ((i - 2) * 30);
              y = 70;
            }
          }
        } else if (poste_label === 'Gardien') {
          x = 50;
          y = 85;
        }
        
        positions.push({
          id: `${poste_label.toLowerCase()}_${i + 1}`,
          label: poste_label.toUpperCase().substring(0, 3),
          x: x,
          y: y,
          poste: poste_label
        });
        
        positionIndex++;
      }
    });
    
    return positions;
  };

  const fieldPositions = generatePositions();
  
  // Récupérer le joueur pour une position donnée depuis playerComposition
  const getPlayerForPosition = (position) => {
    if (!playerComposition || Object.keys(playerComposition).length === 0) return null;
    
    // Chercher le joueur avec la position_key exacte
    const player = playerComposition[position.id];
    
    return player || null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!matchData) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Match non trouvé</Typography>
      </Box>
    );
  }

  const statsSummary = getStatsSummary();

  return (
    <div>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2, 
        bgcolor: '#fff',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <IconButton 
          onClick={() => navigate('/matchs')} 
          sx={{ 
            p: 1,
            minWidth: 'auto',
            width: 'auto'
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <img src="/assets/logo_esma.png" alt="logo" style={{ height: '40px', marginLeft: 'auto', marginRight: 'auto' }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          {matchData.status === 'en_cours' && (
            <IconButton 
              onClick={() => navigate('/match', { state: { matchId: Number(matchId) } })}
              sx={{ 
                p: 1,
                minWidth: 'auto',
                width: 'auto',
                color: '#4caf50'
              }}
              title="Aller aux statistiques du match"
            >
              <BarChartIcon />
            </IconButton>
          )}
          {matchData.status === 'en_cours' && (
            <IconButton 
              onClick={handleShare}
              sx={{ 
                p: 1,
                minWidth: 'auto',
                width: 'auto',
                color: '#ff9800'
              }}
              title="Partager le match en direct"
            >
              <ShareIcon />
            </IconButton>
          )}
        </Box>
        {matchData.status !== 'en_cours' && <Box sx={{ width: '48px' }} />}
      </Box>

      {/* Score */}
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: matchData.status === 'en_cours' ? '#fff3e0' : '#f5f5f5' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            ESMA
          </Typography>
          
          <Typography variant="h4" sx={{ fontWeight: 'bold', mx: 1 }}>
            {matchData.score_home} - {matchData.score_away}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {matchData.label_adversaire}
          </Typography>
        </Box>
        {matchData.status === 'en_cours' && (
          <Box sx={{ mt: 1 }}>
            <Chip
              label={matchData.period === 1 ? '1ère MT' : '2ème MT'}
              color="warning"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary">
          {matchData.match_type} • {matchData.lieu}
        </Typography>
        
        {/* Affichage des buts */}
        {getGoalsWithDetails().length > 0 && (
          <Box sx={{ mt: 2 }}>
            {(() => {
              const homeGoals = getGoalsWithDetails().filter(goal => goal.team === 'home');
              const awayGoals = getGoalsWithDetails().filter(goal => goal.team === 'away');
              const maxGoals = Math.max(homeGoals.length, awayGoals.length);
              
              return Array.from({ length: maxGoals }, (_, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5 
                }}>
                  {/* But de notre équipe (gauche) */}
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    {homeGoals[index] && (
                      <Typography variant="body2" sx={{ fontWeight: 'normal' }}>
                        {homeGoals[index].minutes.join(', ')}' {homeGoals[index].scorer && <span style={{ fontStyle: 'italic' }}>{homeGoals[index].scorer}</span>}
                      </Typography>
                    )}
                  </Box>
                  
                  {/* But de l'équipe adverse (droite) */}
                  <Box sx={{ flex: 1, textAlign: 'right' }}>
                    {awayGoals[index] && (
                      <Typography variant="body2" sx={{ fontWeight: 'normal' }}>
                        {awayGoals[index].minutes.join(', ')}'
                      </Typography>
                    )}
                  </Box>
                </Box>
              ));
            })()}
          </Box>
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            fontSize: '0.8rem',
            minHeight: '48px',
            py: 1,
            px: 0.5
          }
        }}
      >
        <Tab label="Stats" />
        <Tab label="Compo" />
        <Tab label="Stats Joueurs" />
      </Tabs>

      {/* Contenu des tabs */}
      <Box sx={{ p: 2 }}>
        {tabValue === 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Statistiques comparatives
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {Object.entries(statsSummary)
                    .filter(([action]) => !['PASSE DECISIVE', 'Changement'].includes(action))
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([action, counts]) => {
                      const total = counts.home + counts.away;
                      const homePercent = total > 0 ? (counts.home / total) * 100 : 0;
                      const awayPercent = total > 0 ? (counts.away / total) * 100 : 0;

                      return (
                        <TableRow key={action}>
                          <TableCell align="right" sx={{ width: '20%', fontWeight: 'bold' }}>
                            {counts.home}
                          </TableCell>
                          <TableCell sx={{ width: '60%' }}>
                            <Box sx={{ position: 'relative', height: 30, display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  width: `${homePercent}%`,
                                  height: '100%',
                                  bgcolor: '#1976d2',
                                  opacity: 0.7,
                                  borderRadius: '4px 0 0 4px'
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  right: 0,
                                  width: `${awayPercent}%`,
                                  height: '100%',
                                  bgcolor: '#d32f2f',
                                  opacity: 0.7,
                                  borderRadius: '0 4px 4px 0'
                                }}
                              />
                              <Typography
                                sx={{
                                  position: 'absolute',
                                  width: '100%',
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  fontSize: '0.875rem',
                                  zIndex: 1,
                                  color: '#000'
                                }}
                              >
                                {action}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="left" sx={{ width: '20%', fontWeight: 'bold' }}>
                            {counts.away}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Chronologie du match */}
            {getChronologicalEvents().length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                  Chronologie du match
                </Typography>
                
                <Box sx={{ width: '100%' }}>
                  {getChronologicalEvents().map((event, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1,
                      justifyContent: event.team === 'home' ? 'flex-start' : 'flex-end'
                    }}>
                      {event.team === 'home' ? (
                        // Événements de notre équipe (gauche)
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Typography variant="caption" sx={{ minWidth: '40px', fontWeight: 'bold' }}>
                            {event.minute}'
                          </Typography>
                          <Box sx={{ ml: 1 }}>
                            {event.type === 'goal' && (
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                BUT - {event.player}
                                {event.assist && (
                                  <span style={{ fontStyle: 'italic', fontWeight: 'normal' }}> ({event.assist})</span>
                                )}
                              </Typography>
                            )}
                        {event.type === 'substitution' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box component="span" sx={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '0.875rem' }}>
                              ↓ {event.playerOut}
                            </Box>
                            <Box component="span" sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '0.875rem' }}>
                              ↑ {event.playerIn}
                            </Box>
                          </Box>
                        )}
                            {event.type === 'card' && (
                              <Typography variant="body2">
                                <span style={{ color: event.cardType === 'red' ? '#f44336' : '#ff9800' }}>
                                  Carton {event.cardType === 'red' ? 'Rouge' : 'Jaune'}
                                </span> - {event.player}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        // Événements de l'équipe adverse (droite)
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                          <Box sx={{ textAlign: 'right' }}>
                            {event.type === 'goal' && (
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {event.player ? `${event.player} - BUT` : 'BUT'}
                                {event.assist && (
                                  <span style={{ fontStyle: 'italic', fontWeight: 'normal' }}> ({event.assist})</span>
                                )}
                              </Typography>
                            )}
                        {event.type === 'substitution' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row-reverse' }}>
                            <Box component="span" sx={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '0.875rem' }}>
                              ↓ {event.playerOut}
                            </Box>
                            <Box component="span" sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '0.875rem' }}>
                              ↑ {event.playerIn}
                            </Box>
                          </Box>
                        )}
                            {event.type === 'card' && (
                              <Typography variant="body2">
                                {event.player ? `${event.player} - ` : ''}<span style={{ color: event.cardType === 'red' ? '#f44336' : '#ff9800' }}>
                                  Carton {event.cardType === 'red' ? 'Rouge' : 'Jaune'}
                                </span>
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ minWidth: '40px', fontWeight: 'bold', ml: 1 }}>
                            {event.minute}'
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
              Composition de départ
            </Typography>
            
            {/* Terrain */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '400px',
                backgroundColor: '#2e7d32',
                borderRadius: 3,
                border: '3px solid #fff',
                mb: 4,
                overflow: 'hidden'
              }}
            >
              {/* Lignes du terrain */}
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: '2px',
                backgroundColor: '#fff',
                transform: 'translateY(-50%)'
              }} />
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '20px',
                height: '20px',
                backgroundColor: '#fff',
                borderRadius: '50%',
                transform: 'translate(-50%, -50%)'
              }} />

              {/* Joueurs */}
              {fieldPositions.map((position) => {
                const player = getPlayerForPosition(position);
                return (
                  <Box
                    key={position.id}
                    sx={{
                      position: 'absolute',
                      left: `${position.x}%`,
                      top: `${position.y}%`,
                      transform: 'translate(-50%, -50%)',
                      minWidth: '80px',
                      textAlign: 'center'
                    }}
                  >
                    {player ? (
                      <Box>
                        <Typography
                          sx={{
                            color: '#fff',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            borderRadius: 1,
                            padding: '4px 8px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {player.firstname}. {player.lastname?.charAt(0)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          color: '#fff',
                          backgroundColor: 'rgba(0,0,0,0.3)',
                          borderRadius: 1,
                          padding: '4px 8px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {position.label}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Remplaçants */}
            {substitutes.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Remplaçants
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {substitutes.map((sub) => (
                    <Box
                      key={sub.rowid}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          bgcolor: '#757575',
                          border: '2px solid white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}
                      >
                        {sub.firstname[0]}{sub.lastname[0]}
                      </Box>
                      <Typography variant="body2">
                        {sub.firstname} {sub.lastname}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Changements */}
            {substitutions.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Changements
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {substitutions.map((sub) => (
                    <Box
                      key={sub.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1
                      }}
                    >
                      <Chip 
                        label={sub.minute} 
                        size="small" 
                        sx={{ 
                          bgcolor: '#ff9800', 
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: '50px'
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <Box component="span" sx={{ color: '#d32f2f' }}>↓ {sub.player_out_name}</Box>
                        {' → '}
                        <Box component="span" sx={{ color: '#4caf50' }}>↑ {sub.player_in_name}</Box>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

          </Box>
        )}

        {/* Onglet Stats Joueurs */}
        {tabValue === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Statistiques par joueur
            </Typography>
            
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: 'white', zIndex: 1 }}>Joueur</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>BUT</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>PD</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>SA</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>TC</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>TNC</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>CJ</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>CR</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>CG</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>CD</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>CenG</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>CenD</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>FS</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>FC</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Utiliser tous les joueurs du match (déjà récupérés via l'API)
                    return allPlayers.map(player => {
                      const playerStats = {
                        but: 0,
                        passeDecisive: 0,
                        sauvetage: 0,
                        tirCadre: 0,
                        tirNonCadre: 0,
                        cartonJaune: 0,
                        cartonRouge: 0,
                        cornerGauche: 0,
                        cornerDroit: 0,
                        centreGauche: 0,
                        centreDroit: 0,
                        fauteSubit: 0,
                        fauteCommise: 0
                      };
                      
                      stats.forEach(stat => {
                        // Ignorer les statistiques sans joueur associé (id_player null)
                        if (stat.id_player === null) {
                          return;
                        }
                        
                        if (Number(stat.id_player) === Number(player.id_player)) {
                          if (stat.action === 'BUT') playerStats.but++;
                          if (stat.action === 'PASSE DECISIVE') playerStats.passeDecisive++;
                          if (stat.action === 'Sauvetage/Arrêt') playerStats.sauvetage++;
                          if (stat.action === 'Tir Cadre') playerStats.tirCadre++;
                          if (stat.action === 'Tir Non Cadre') playerStats.tirNonCadre++;
                          if (stat.action === 'Carton Jaune') playerStats.cartonJaune++;
                          if (stat.action === 'Carton Rouge') playerStats.cartonRouge++;
                          if (stat.action === 'Corner (gauche)') playerStats.cornerGauche++;
                          if (stat.action === 'Corner (droit)') playerStats.cornerDroit++;
                          if (stat.action === 'Centre (gauche)') playerStats.centreGauche++;
                          if (stat.action === 'Centre (droit)') playerStats.centreDroit++;
                          if (stat.action === 'Faut subit') playerStats.fauteSubit++;
                          if (stat.action === 'Faut commise') playerStats.fauteCommise++;
                        }
                      });
                      
                      return (
                        <TableRow
                          key={player.id_player}
                          onClick={() => navigate(`/joueur/${player.id_player}`)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: '#f5f5f5'
                            }
                          }}
                        >
                          <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'white', zIndex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                              {player.firstname} {player.lastname.charAt(0).toUpperCase()}.
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{playerStats.but}</TableCell>
                          <TableCell align="center">{playerStats.passeDecisive}</TableCell>
                          <TableCell align="center">{playerStats.sauvetage}</TableCell>
                          <TableCell align="center">{playerStats.tirCadre}</TableCell>
                          <TableCell align="center">{playerStats.tirNonCadre}</TableCell>
                          <TableCell align="center">{playerStats.cartonJaune}</TableCell>
                          <TableCell align="center">{playerStats.cartonRouge}</TableCell>
                          <TableCell align="center">{playerStats.cornerGauche}</TableCell>
                          <TableCell align="center">{playerStats.cornerDroit}</TableCell>
                          <TableCell align="center">{playerStats.centreGauche}</TableCell>
                          <TableCell align="center">{playerStats.centreDroit}</TableCell>
                          <TableCell align="center">{playerStats.fauteSubit}</TableCell>
                          <TableCell align="center">{playerStats.fauteCommise}</TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
            
            {allPlayers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                Aucun joueur pour ce match
              </Typography>
            )}
          </Box>
        )}


      </Box>
    </div>
  );
}

